import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, TableWrap, Th, Td, Loading, Badge } from "../components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

function CatManager({ endpoint, label }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setItems(data); } finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (edit?.id) await api.put(`${endpoint}/${edit.id}`, { name, subcategories: edit.subcategories || [], status: edit.status || "active" });
      else await api.post(endpoint, { name, subcategories: [], status: "active" });
      toast.success("Saved"); setEdit(null); setName(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => { setEdit({}); setName(""); }} data-testid={`add-${label}`} className="bg-[#0F2942] gap-1.5"><Plus className="h-4 w-4" /> Add {label}</Button>
      </div>
      {loading ? <Loading /> : (
        <TableWrap>
          <thead><tr><Th>Name</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id}><Td className="font-medium">{c.name}</Td><Td><Badge cls={c.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}>{c.status}</Badge></Td>
              <Td><button onClick={() => { setEdit(c); setName(c.name); }} className="text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button></Td></tr>
          ))}</tbody>
        </TableWrap>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent><DialogHeader><DialogTitle>{edit?.id ? "Edit" : "Add"} {label}</DialogTitle></DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name" data-testid="cat-name-input" />
          {edit?.id && <select value={edit.status || "active"} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="h-9 rounded-lg border px-2 text-sm"><option value="active">Active</option><option value="inactive">Inactive</option></select>}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={!name} data-testid="cat-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Branches() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", address: "" });
  const load = () => api.get("/branches").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit?.id) await api.put(`/branches/${edit.id}`, { ...form, status: "active" });
      else await api.post("/branches", { ...form, status: "active" });
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3"><Button size="sm" onClick={() => { setEdit({}); setForm({ name: "", address: "" }); }} data-testid="add-branch" className="bg-[#0F2942] gap-1.5"><Plus className="h-4 w-4" /> Add Branch</Button></div>
      <TableWrap><thead><tr><Th>Name</Th><Th>Address</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody>{items.map((b) => (<tr key={b.id}><Td className="font-medium">{b.name}</Td><Td>{b.address}</Td><Td><Badge cls="bg-emerald-50 text-emerald-700 border-emerald-200">{b.status}</Badge></Td>
          <Td><button onClick={() => { setEdit(b); setForm({ name: b.name, address: b.address }); }} className="text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button></Td></tr>))}</tbody></TableWrap>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent><DialogHeader><DialogTitle>{edit?.id ? "Edit" : "Add"} Branch</DialogTitle></DialogHeader>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Branch name" />
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" />
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={!form.name}>Save</Button></DialogFooter>
        </DialogContent></Dialog>
    </div>
  );
}

export default function MasterData() {
  return (
    <div>
      <PageHeader title="Master Data" subtitle="Manage income categories, expense categories and branches" />
      <Tabs defaultValue="income">
        <TabsList>
          <TabsTrigger value="income" data-testid="tab-income-cats">Income Categories</TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expense-cats">Expense Categories</TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches">Branches</TabsTrigger>
        </TabsList>
        <TabsContent value="income" className="mt-4"><CatManager endpoint="/income-categories" label="Income Category" /></TabsContent>
        <TabsContent value="expense" className="mt-4"><CatManager endpoint="/expense-categories" label="Expense Category" /></TabsContent>
        <TabsContent value="branches" className="mt-4"><Branches /></TabsContent>
      </Tabs>
    </div>
  );
}
